/**
 * VENTARA — Brand mark.
 *
 * Four arcs, evenly spaced with small gaps, circling a central 4-point
 * spark on a single true radius. Uses the brand gradient (Aurora Magenta
 * → Solar Pink → Solar Orange) with a Quantum Violet accent. Renders
 * monochrome when `mono` is true.
 *
 * The mark reads confidently from 16px (favicon) up to hero scale.
 */
import { motion } from "framer-motion";

let _uid = 0;
const nextId = () => `vm-${++_uid}`;

export default function VentaraMark({
    size = 22,
    className = "",
    mono = false,
    animate = false,
    title = "Ventara",
}) {
    // Unique gradient ids to allow multiple marks on the same page.
    const sparkId = nextId();
    const arcA = nextId();
    const arcB = nextId();
    const arcC = nextId();

    const spark = mono ? "currentColor" : `url(#${sparkId})`;
    const strokeA = mono ? "currentColor" : `url(#${arcA})`;
    const strokeB = mono ? "currentColor" : `url(#${arcB})`;
    const strokeC = mono ? "currentColor" : `url(#${arcC})`;
    const dotFill = mono ? "currentColor" : undefined;

    const MotionPath = animate ? motion.path : "path";
    const MotionSpark = animate ? motion.path : "path";

    return (
        <svg
            viewBox="0 0 100 100"
            width={size}
            height={size}
            className={className}
            role="img"
            aria-label={title}
        >
            {!mono && (
                <defs>
                    {/* Central spark — magenta → orange sunrise */}
                    <linearGradient id={sparkId} x1="0.1" y1="0" x2="0.9" y2="1">
                        <stop offset="0%" stopColor="#FF4DA6" />
                        <stop offset="55%" stopColor="#FF8A00" />
                        <stop offset="100%" stopColor="#FFB84D" />
                    </linearGradient>
                    {/* Outer arcs — violets */}
                    <linearGradient id={arcA} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#8A2BE2" />
                        <stop offset="100%" stopColor="#FF4DA6" />
                    </linearGradient>
                    <linearGradient id={arcB} x1="1" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6A5CFF" />
                        <stop offset="100%" stopColor="#8A2BE2" />
                    </linearGradient>
                    <linearGradient id={arcC} x1="1" y1="0" x2="0.2" y2="1">
                        <stop offset="0%" stopColor="#FF8A00" />
                        <stop offset="100%" stopColor="#FF4DA6" />
                    </linearGradient>
                </defs>
            )}

            {/* Four arcs circling the spark, evenly spaced with small gaps,
                all on one true radius (36 units from center). */}
            {/* NE arc */}
            <MotionPath
                d="M 85.45 56.25 A 36 36 0 0 1 56.25 85.45"
                stroke={strokeA}
                strokeWidth="2.4"
                strokeLinecap="round"
                fill="none"
                opacity="0.9"
                {...(animate && {
                    initial: { pathLength: 0, opacity: 0 },
                    animate: { pathLength: 1, opacity: 0.9 },
                    transition: { duration: 1.4, ease: [0.22, 1, 0.36, 1] },
                })}
            />
            <circle cx="85.45" cy="56.25" r="3" fill={dotFill || "#8A2BE2"} />
            <circle cx="56.25" cy="85.45" r="3" fill={dotFill || "#FF4DA6"} />

            {/* SE arc */}
            <MotionPath
                d="M 43.75 85.45 A 36 36 0 0 1 14.55 56.25"
                stroke={strokeB}
                strokeWidth="2.4"
                strokeLinecap="round"
                fill="none"
                opacity="0.9"
                {...(animate && {
                    initial: { pathLength: 0, opacity: 0 },
                    animate: { pathLength: 1, opacity: 0.9 },
                    transition: { duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.1 },
                })}
            />
            <circle cx="43.75" cy="85.45" r="3" fill={dotFill || "#6A5CFF"} />
            <circle cx="14.55" cy="56.25" r="3" fill={dotFill || "#8A2BE2"} />

            {/* SW arc */}
            <MotionPath
                d="M 14.55 43.75 A 36 36 0 0 1 43.75 14.55"
                stroke={strokeC}
                strokeWidth="2.4"
                strokeLinecap="round"
                fill="none"
                opacity="0.9"
                {...(animate && {
                    initial: { pathLength: 0, opacity: 0 },
                    animate: { pathLength: 1, opacity: 0.9 },
                    transition: { duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 },
                })}
            />
            <circle cx="14.55" cy="43.75" r="3" fill={dotFill || "#FF8A00"} />
            <circle cx="43.75" cy="14.55" r="3" fill={dotFill || "#FF4DA6"} />

            {/* NW arc */}
            <MotionPath
                d="M 56.25 14.55 A 36 36 0 0 1 85.45 43.75"
                stroke={strokeA}
                strokeWidth="2.4"
                strokeLinecap="round"
                fill="none"
                opacity="0.9"
                {...(animate && {
                    initial: { pathLength: 0, opacity: 0 },
                    animate: { pathLength: 1, opacity: 0.9 },
                    transition: { duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.3 },
                })}
            />
            <circle cx="56.25" cy="14.55" r="3" fill={dotFill || "#8A2BE2"} />
            <circle cx="85.45" cy="43.75" r="3" fill={dotFill || "#FF4DA6"} />

            {/* Central 4-point spark */}
            <MotionSpark
                d="M 50 28 Q 52 48 72 50 Q 52 52 50 72 Q 48 52 28 50 Q 48 48 50 28 Z"
                fill={spark}
                {...(animate && {
                    initial: { scale: 0.4, opacity: 0 },
                    animate: { scale: [0.4, 1.08, 1], opacity: [0, 1, 1] },
                    transition: { duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.4 },
                    style: { transformOrigin: "50% 50%" },
                })}
            />
        </svg>
    );
}
